/**
 * SDK-wide constants — single source of truth for limits, defaults and
 * backend-defined magic values.
 */

// ── API endpoints ────────────────────────────────────────────────────────────

export const ENDPOINTS = {
  SESSION_CREATE:      "/v1/session/create/",
  SDK_INITIALIZE:      "/v1/initialize/",
  ONBOARDING_VERIFY:   "/v1/onboarding/verify/",
  LIVENESS_CREATE:     "/v1/sdk/liveness/create/",
  LIVENESS_SUBMIT:     "/v1/sdk/liveness/submit/",
  AVAILABLE_CHANNELS:  "/v1/available-channels/",
} as const;

// ── Session ──────────────────────────────────────────────────────────────────

/** Backend session TTL in milliseconds (30 minutes). */
export const SESSION_TTL_MS = 30 * 60 * 1000;

/** Warn the user this many ms before session expires. */
export const SESSION_WARN_BEFORE_MS = 2 * 60 * 1000;

// ── Network ──────────────────────────────────────────────────────────────────

/** Default request timeout in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Number of automatic retries on transient network errors. */
export const MAX_RETRIES = 2;

/** Delay between retries in milliseconds. */
export const RETRY_DELAY_MS = 1_000;

// ── File uploads ─────────────────────────────────────────────────────────────

/** Maximum document image size the backend accepts (5 MB). */
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

/** Maximum video size the backend accepts (50 MB). */
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

/** Maximum snapshot / autoshot size (2 MB). */
export const MAX_SNAPSHOT_BYTES = 2 * 1024 * 1024;

/** Accepted MIME types for document images. */
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/** JPEG quality used when encoding captured frames (0–1). */
export const CAPTURE_JPEG_QUALITY = 0.92;

// ── Liveness challenge ───────────────────────────────────────────────────────

/** Backend-accepted challenge actions (UPPERCASE). */
export const CHALLENGE_ACTIONS = ["BLINK", "TURN_LEFT", "TURN_RIGHT", "TURN_HEAD", "OPEN_MOUTH"] as const;

/** Default set sent when the caller does not specify actions. */
export const DEFAULT_CHALLENGE_ACTIONS = ["BLINK", "TURN_LEFT", "OPEN_MOUTH"] as const;

/** Time limit per liveness attempt in seconds. */
export const LIVENESS_TIME_LIMIT_SECONDS = 45;

/** Milliseconds to pause after all actions before capturing the final snapshot. */
export const POST_ACTION_SETTLE_MS = 800;

// ── Camera ───────────────────────────────────────────────────────────────────

/** Ideal resolution for the front-facing liveness camera. */
export const CAMERA_IDEAL_WIDTH  = 640;
export const CAMERA_IDEAL_HEIGHT = 480;

/** Ideal resolution for rear-facing document capture camera. */
export const DOC_CAMERA_IDEAL_WIDTH  = 1280;
export const DOC_CAMERA_IDEAL_HEIGHT = 960;

/** Video bitrate used by MediaRecorder for liveness recordings (~1.4 MB / 45 s). */
export const RECORDING_BITS_PER_SECOND = 250_000;

// ── Face detection ───────────────────────────────────────────────────────────

/** Max milliseconds to wait for an initial face detection before proceeding. */
export const FACE_DETECT_TIMEOUT_MS = 10_000;

/** Polling interval for the face-present check (ms). */
export const FACE_DETECT_POLL_MS = 100;

// ── UI / UX ──────────────────────────────────────────────────────────────────

/** How long (ms) the success overlay is shown before the flow advances. */
export const RESULT_OVERLAY_DURATION_MS = 2_800;

/** localStorage key used to persist the browser fingerprint ID. */
export const FINGERPRINT_STORAGE_KEY = "_adhere_fid";

/** z-index for the SDK overlay — high enough to sit above any app content. */
export const OVERLAY_Z_INDEX = 99_999;
